import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import { Lightning, CheckCircle, ArrowRight } from "@phosphor-icons/react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export function UpgradePro({ onClose }) {
  const { user } = useAuth();
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    axios.get(`${API}/payments/plan`).then((r) => setPlan(r.data)).catch(() => {});
  }, []);

  const handleUpgrade = async () => {
    setLoading(true);
    try {
      const origin = window.location.origin;
      const { data } = await axios.post(`${API}/payments/checkout`, { origin_url: origin });
      if (data.url) {
        window.location.href = data.url;
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (typeof detail === "string") {
        alert(detail);
      }
      setLoading(false);
    }
  };

  if (!user || user.role === "admin" || user.role === "pro") return null;

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="upgrade-modal">
      <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="bg-neutral-950 border border-neutral-800 max-w-lg w-full">
        {/* Header */}
        <div className="p-6 border-b border-neutral-800">
          <div className="flex items-center gap-3">
            <Lightning size={28} weight="duotone" className="text-yellow-400" />
            <div>
              <h2 className="text-xl font-bold" data-testid="upgrade-title">Upgrade to Pro</h2>
              <p className="text-sm text-neutral-400">Unlock the full power of BusyBodies AI</p>
            </div>
          </div>
        </div>

        {/* Benefits */}
        <div className="p-6 space-y-3">
          {[
            "500 chat messages/day (10x free)",
            "25 AI videos/day",
            "50 book chapters/day",
            "20 AI book covers/day",
            "100 agent analyses/day",
            "50 autonomous tasks/day",
            "100 file uploads/day",
            "20 Netlify sites/day",
          ].map((benefit) => (
            <div key={benefit} className="flex items-center gap-3 text-sm">
              <CheckCircle size={18} weight="fill" className="text-green-500 shrink-0" />
              <span className="text-neutral-300">{benefit}</span>
            </div>
          ))}
        </div>

        {/* Price */}
        <div className="px-6 pb-6">
          {plan && (
            <div className="text-center mb-4">
              <span className="text-4xl font-bold">${plan.price}</span>
              <span className="text-neutral-400 text-sm ml-1">/month</span>
            </div>
          )}
          <Button onClick={handleUpgrade} disabled={loading} className="w-full h-12 bg-white text-black hover:bg-neutral-200 font-semibold text-sm" data-testid="upgrade-checkout-button">
            {loading ? "Redirecting to checkout..." : <>Upgrade Now <ArrowRight size={18} className="ml-2" /></>}
          </Button>
          <button onClick={onClose} className="w-full mt-3 text-center text-sm text-neutral-500 hover:text-neutral-300 transition-colors" data-testid="upgrade-close-button">
            Maybe later
          </button>
        </div>
      </motion.div>
    </div>
  );
}

// Inline upgrade banner for the usage bar
export function UpgradeBanner({ onUpgradeClick }) {
  return (
    <button onClick={onUpgradeClick} className="w-full mt-3 flex items-center justify-between p-3 border border-yellow-800/50 bg-yellow-900/10 hover:bg-yellow-900/20 transition-colors group" data-testid="upgrade-banner">
      <div className="flex items-center gap-2">
        <Lightning size={16} weight="fill" className="text-yellow-400" />
        <span className="text-xs font-semibold text-yellow-300">Upgrade to Pro for 10x limits</span>
      </div>
      <ArrowRight size={14} className="text-yellow-400 group-hover:translate-x-1 transition-transform" />
    </button>
  );
}
